import { Injectable, inject } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DictionaryItem } from 'src/app/models/dictionary-item';
import { WorkerFormAdditionalData } from 'src/app/models/worker-form-additional-data';
import { autocompleteValidator } from 'src/app/shared/validators/autocomplete.validator';
import { FirstNameValidator } from 'src/app/shared/validators/first-name.validator';
import { fullNameValidator } from 'src/app/shared/validators/full-name.validator';
import { LastNameValidator } from 'src/app/shared/validators/last-name.validator';
import { bankAccountValidator } from '../bank-account.validator';
import { AddressTypeEnum } from 'src/app/models/AddressTypeEnum';
import { DocumentDeliveryMethodEnum } from 'src/app/models/enums/document-delivery-method-enum';
import { hrNumberFormatValidator, hrNumberUniquenessValidator } from 'src/app/workers/worker-form/hr-number.validator';
import { WorkerFormService } from 'src/app/data/worker-form.service';
import { BehaviorSubject, filter, firstValueFrom, Subscription, take } from 'rxjs';
import { HrNumberDto } from 'src/app/models/dtos/hr-number-dto';

@Injectable()
export class AdditionalDataFormGroupService {
  private formBuilder = inject(UntypedFormBuilder);
  private workerFormService = inject(WorkerFormService);

  public set institutions(value: DictionaryItem[]) {
    this.institutions$.next(value);
  }
  public get institutions(): DictionaryItem[] {
    return this.institutions$.value;
  }
  private institutions$ = new BehaviorSubject<DictionaryItem[]>([]);
  private subs: Subscription[] = [];
  private runAsyncCheck = false;

  constructor() { }

  dispose(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  buildFormGroup(isFormDisabled: boolean) {
    return this.formBuilder.group(
      {
        nationalHealthFund: [{ value: null, disabled: isFormDisabled }, Validators.required],
        taxOffice: [{ value: null, disabled: isFormDisabled }, [Validators.required, autocompleteValidator]],
        bankAccountNumber: [{ value: null, disabled: isFormDisabled }, [bankAccountValidator]],
        bankAccountOwnerFirstName: [{ value: null, disabled: isFormDisabled }, [Validators.maxLength(50), FirstNameValidator()]],
        bankAccountOwnerLastName: [{ value: null, disabled: isFormDisabled }, [Validators.maxLength(50), LastNameValidator()]],
        emergencyContactFirstName: [{ value: null, disabled: isFormDisabled }, [Validators.maxLength(50), FirstNameValidator()]],
        emergencyContactLastName: [{ value: null, disabled: isFormDisabled }, [Validators.maxLength(50), LastNameValidator()]],
        emergencyContactPhone: [{ value: null, disabled: isFormDisabled }, [Validators.pattern('^[0-9]{7,12}$')]],
        emergencyContactPhoneCodeId: [{ value: null, disabled: isFormDisabled }],
        emergencyContactAddress: [{ value: null, disabled: isFormDisabled }, Validators.maxLength(100)],
        declarationAddressType: [{ value: AddressTypeEnum.Residential, disabled: true }, [Validators.required]],
        addressToSendDeclarationAddressType: [{ value: null, disabled: true }, [Validators.required]],
        emailToSendDeclaration: [{ value: null, disabled: true }, [Validators.required, Validators.maxLength(50), Validators.email]],
        documentDeliveryMethod: [{ value: null, disabled: true }, [Validators.required]],
        isBankAccountDoesntBelongToWorker: [false],
        hrNumbers: this.formBuilder.array([]),
      },
      {
        validators: [
          fullNameValidator('bankAccountOwnerFirstName', 'bankAccountOwnerLastName', 'bankAccountOwnerFullNameInvalid')
        ],
      },
    );
  }

  setFormGroupValue(fg: UntypedFormGroup, additionalData: WorkerFormAdditionalData, workerId: number, isSelfWorkerForm: boolean): void {
    fg.patchValue({
      nationalHealthFund: additionalData.NationalHealthFundId,
      taxOffice: additionalData.TaxOfficeId
        ? ({
          Id: additionalData.TaxOfficeId,
          Name: additionalData.TaxOfficeName,
        } as DictionaryItem)
        : null,
      bankAccountNumber: additionalData.BankAccountNumber,
      bankAccountOwnerFirstName: additionalData.BankAccountOwnerFirstName,
      bankAccountOwnerLastName: additionalData.BankAccountOwnerLastName,
      emergencyContactFirstName: additionalData.EmergencyContactFirstName,
      emergencyContactLastName: additionalData.EmergencyContactLastName,
      emergencyContactPhoneCodeId: additionalData.EmergencyContactPhoneCodeId,
      emergencyContactPhone: additionalData.EmergencyContactPhone,
      emergencyContactAddress: additionalData.EmergencyContactAddress,
      emailToSendDeclaration: additionalData.EmailToSendDeclaration,
      declarationAddressType: additionalData.DeclarationAddressTypeId ?? AddressTypeEnum.Residential,
      addressToSendDeclarationAddressType: additionalData.AddressToSendDeclarationAddressTypeId,
      documentDeliveryMethod: additionalData.DocumentDeliveryMethodId,
      isBankAccountDoesntBelongToWorker: !!additionalData.BankAccountOwnerFirstName || !!additionalData.BankAccountOwnerLastName,
    }, { emitEvent: false });

    this.subs.forEach(s => s.unsubscribe());
    const hrNumbersArray = fg.get('hrNumbers') as UntypedFormArray;
    hrNumbersArray.clear();

    if (!isSelfWorkerForm) {
      this.initHrNumbersForEdit(fg, additionalData.HrNumbers, workerId);
    }

    if (!!additionalData.BankAccountOwnerFirstName || !!additionalData.BankAccountOwnerLastName) {
      fg.get('isBankAccountDoesntBelongToWorker').enable({ emitEvent: false });
    }

    if (additionalData.HasDeclarationAddressConfigEnabled) {
      fg.get('declarationAddressType').enable();
      fg.get('documentDeliveryMethod').enable();

      if (additionalData.DocumentDeliveryMethodId === DocumentDeliveryMethodEnum.PaperForm) {
        fg.get('addressToSendDeclarationAddressType').enable();
      } else if (additionalData.DocumentDeliveryMethodId === DocumentDeliveryMethodEnum.ElectronicForm) {
        fg.get('emailToSendDeclaration').enable();
      }
    }
  }

  updateBankAccountValidators(fg: UntypedFormGroup, isBankAccountRequired: boolean) {
    const validators = isBankAccountRequired ? [Validators.required, bankAccountValidator] : [bankAccountValidator];
    fg.get('bankAccountNumber').clearValidators();
    fg.get('bankAccountNumber').setValidators(validators);
    fg.get('bankAccountNumber').updateValueAndValidity();
  }

  initHrNumbersForEdit(fg: UntypedFormGroup, rows: HrNumberDto[] | null | undefined, workerId: number): void {
    const createRows = (institutions: DictionaryItem[]) => {
      for (const inst of institutions) {
        const hr = existingMap.get(inst.Id) ?? '';
        hrNumbers.push(this.createRow(fg, inst.Id, hr, fg.disabled, workerId));
      }
      fg.updateValueAndValidity({ emitEvent: false });
      fg.markAsPristine();
      fg.markAsUntouched();
    };

    const hrNumbers = fg.get('hrNumbers') as FormArray;
    this.subs.forEach(s => s.unsubscribe());
    this.subs = [];
    while (hrNumbers.length > 0) hrNumbers.removeAt(hrNumbers.length - 1);

    const existingMap = new Map<number, string>();
    (rows ?? []).forEach(r => {
      if (r?.InstitutionId != null) {
        existingMap.set(r.InstitutionId, (r?.HrNumber ?? '').trim());
      }
    });

    const current = this.institutions ?? [];
    if (current.length > 0) {
      createRows(current);
      return;
    }

    const instSub = this.institutions$
      .pipe(
        filter(insts => !!insts && insts.length > 0),
        take(1)
      )
      .subscribe(insts => createRows(insts));

    this.subs.push(instSub);
  }

  private createRow(
    fg: UntypedFormGroup,
    instId: number | null = null,
    hr: string = '',
    isFormDisabled: boolean = false,
    workerId?: number): FormGroup {
    const hrNumbers = fg.get('hrNumbers') as FormArray;

    const group = this.formBuilder.group({
      institutionId: [instId],
      hrNumber: [{ value: hr, disabled: isFormDisabled },
      [Validators.maxLength(20), hrNumberFormatValidator()],
      [hrNumberUniquenessValidator(this.workerFormService, workerId!, () => this.runAsyncCheck)]
      ]
    });

    const sub = (group.get('hrNumber') as FormControl).valueChanges.subscribe(val => {
      this.clearAsyncHrNumberErrors(group.get('hrNumber') as FormControl);
    });

    this.subs.push(sub);
    return group;
  }

  public clearAsyncHrNumberErrors(ctrl: AbstractControl): void {
    const errs = ctrl.errors;
    if (!errs) {
      return;
    }

    const { hrNumberDuplicate, hrNumberValidationError, ...rest } = errs;

    if (hrNumberDuplicate || hrNumberValidationError) {
      const next = Object.keys(rest).length ? rest : null;
      ctrl.setErrors(next);
      ctrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
  }

  public async runAsyncSubmitValidation(form: FormGroup): Promise<void> {

    this.runAsyncCheck = true;

    form.markAllAsTouched();
    form.updateValueAndValidity({ onlySelf: false, emitEvent: true });

    const kickControl = (ctrl: AbstractControl) => ctrl.updateValueAndValidity({ onlySelf: true, emitEvent: true });

    const hrNumbers = form.get('hrNumbers') as FormArray | null;
    if (hrNumbers) {
      hrNumbers.controls.forEach(row => {
        const hr = row.get('hrNumber');
        if (hr && !hr.disabled) kickControl(hr);
        const inst = row.get('institutionId');
        if (inst && !inst.disabled) kickControl(inst);
      });
    }

    if (form.pending) {
      await firstValueFrom(
        form.statusChanges.pipe(
          filter(s => s !== 'PENDING'),
          take(1)
        )
      );
    }

    this.runAsyncCheck = false;
  }
}
